/* ============================================================
   Whisk quote cart — runtime
   Extracted verbatim from layout/theme.liquid (was an inline <script>, lines 850-1463)
   on 2026-08-24. The class body is unchanged; only the initialisation tail is new,
   and it adds readiness signalling without altering behaviour.

   Six files consume this. See docs/adr/ and CLAUDE.md.
   ============================================================ */

/* ================================================================
   GLOBAL QUOTE CART SYSTEM — FULL VERSION (NO LIQUID)
   Popup, Form, Table, Email Builder, Buttons, Radio/Checkbox
   EXACT DOM structure maintained for CSS compatibility
================================================================ */

class CompleteQuoteCart {
  constructor() {
    this.key = "complete_quote_cart";
    this.formKey = "complete_quote_form"; // persists the popup contact form across reloads + product nav
    this.items = JSON.parse(localStorage.getItem(this.key)) || [];
    this.headerQuoteBadge = null;
    this.desktopHeaderQuoteBadge = null;

    this.setupEventDelegation();
    this.updateBadge();
    this.updateHeaderBadge();
    this.updateDesktopHeaderBadge(); // Update desktop badge on initialization
  }

  /* -----------------------------------------------------------
     EVENT DELEGATION
  ----------------------------------------------------------- */
  setupEventDelegation() {
    document.addEventListener("click", (e) => {
      /* REMOVE ITEM */
      const removeBtn = e.target.closest(".remove-btn");
      if (removeBtn) {
        const row = removeBtn.closest(".quote-item-row");
        this.removeProduct(row.dataset.product, row.dataset.variant);
        return;
      }

      /* QTY BUTTONS */
      if (e.target.classList.contains("qty-btn")) {
        const row = e.target.closest(".quote-item-row");
        const input = row.querySelector(".qty-input");
        let qty = parseInt(input.value) || 1;

        if (e.target.classList.contains("plus")) qty++;
        if (e.target.classList.contains("minus")) qty = Math.max(1, qty - 1);

        input.value = qty;
        this.updateQuantity(row.dataset.product, row.dataset.variant, qty);
        return;
      }

      /* OPEN POPUP (HEADER ICON) */
      if (e.target.closest("#headerQuoteButton")) {
        e.preventDefault();
        this.openPopup();
        return;
      }

      /* CONVERT CART -> QUOTE */
      if (e.target.id === "goToQuotePageBtn") {
        e.preventDefault();
        this.convertCartToQuote(e.target);
        return;
      }
    });

    /* DIRECT QUANTITY INPUT */
    document.addEventListener("change", (e) => {
      if (e.target.classList.contains("qty-input")) {
        const row = e.target.closest(".quote-item-row");
        this.updateQuantity(
          row.dataset.product,
          row.dataset.variant,
          parseInt(e.target.value) || 1
        );
      }
    });

    /* ESC CLOSE */
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.closePopup();
    });

    /* FORM SUBMISSION - NEW: Handle form submit to add product data */
    document.addEventListener("submit", (e) => {
      const form = e.target.closest("#quoteContactForm");
      if (form) {
        e.preventDefault();
        if (this.items.length === 0) return;
        this.handleFormSubmit(form);
      }
    });
  }

  /* -----------------------------------------------------------
     CART STORAGE + COUNTERS
  ----------------------------------------------------------- */
  save() {
    localStorage.setItem(this.key, JSON.stringify(this.items));
  }

  updateBadge() {
    const count = this.items.length;
    const btn = document.querySelector("#openQuotePopup");
    if (!btn) return;

    let badge = btn.querySelector(".quote-badge");
    if (!badge) {
      badge = document.createElement("span");
      badge.className = "quote-badge";
      btn.appendChild(badge);
    }

    badge.textContent = count;
    badge.style.display = count > 0 ? "block" : "none";
  }

  updateHeaderBadge() {
    const badge = document.getElementById("headerQuoteBadge");
    if (!badge) return;

    badge.textContent = this.items.length;
    badge.classList.toggle("hidden", this.items.length === 0);
  }

  /* -----------------------------------------------------------
     UPDATE DESKTOP BADGE
  ----------------------------------------------------------- */
  updateDesktopHeaderBadge() {
    const badge = document.getElementById("newheaderQuoteBadge");
    if (!badge) return;

    badge.textContent = this.items.length;
    badge.classList.toggle("hidden", this.items.length === 0);
  }

  /* -----------------------------------------------------------
     ADD / REMOVE / UPDATE CART
  ----------------------------------------------------------- */
  addProduct(product) {
    const exists = this.items.find(
      (i) => i.product_id == product.product_id && i.variant_id == product.variant_id
    );

    if (exists) exists.quantity += product.quantity;
    else this.items.push(product);

    this.save();
    this.updateBadge();
    this.updateHeaderBadge();
    this.updateDesktopHeaderBadge(); // Update desktop badge when product is added

    setTimeout(() => this.openPopup(), 150);
  }

  /* -----------------------------------------------------------
     CONVERT CART -> QUOTE
     Pulls every line item from the Shopify cart and adds it to the quote.
     Existing quote items are kept; matching items just have their quantity
     increased. The Shopify cart itself is left untouched.
  ----------------------------------------------------------- */
  async convertCartToQuote(button) {
    const original = button ? button.textContent : "";
    if (button) { button.disabled = true; button.textContent = "Adding cart…"; }

    try {
      const res = await fetch("/cart.js", { headers: { Accept: "application/json" } });
      const cart = await res.json();

      if (!cart.items || cart.items.length === 0) {
        if (button) { button.textContent = "Your cart is empty"; }
        setTimeout(() => { if (button) { button.disabled = false; button.textContent = original; } }, 1500);
        return;
      }

      cart.items.forEach((ci) => {
        const price = (ci.final_price != null ? ci.final_price : ci.price);
        const item = {
          product_id: String(ci.product_id),
          variant_id: String(ci.variant_id != null ? ci.variant_id : ci.id),
          quantity: ci.quantity,
          image: ci.image || (ci.featured_image && ci.featured_image.url) || "",
          title: ci.product_title || ci.title,
          sku: ci.sku || "",
          price: price,
          formatted_price: "$" + (price / 100).toFixed(2),
          dimensions: ""
        };

        const exists = this.items.find(
          (i) => i.product_id == item.product_id && i.variant_id == item.variant_id
        );
        if (exists) exists.quantity += item.quantity;
        else this.items.push(item);
      });

      this.save();
      this.updateBadge();
      this.updateHeaderBadge();
      this.updateDesktopHeaderBadge();
      this.renderPopup();
      this.openPopup();
    } catch (err) {
      console.error("Cart → quote conversion failed", err);
      if (button) { button.textContent = "Couldn't add cart"; }
    } finally {
      setTimeout(() => { if (button) { button.disabled = false; button.textContent = original; } }, 1500);
    }
  }

  removeProduct(pid, vid) {
    this.items = this.items.filter((i) => !(i.product_id == pid && i.variant_id == vid));
    this.save();
    this.updateBadge();
    this.updateHeaderBadge();
    this.updateDesktopHeaderBadge(); // Update desktop badge when product is removed
    this.renderPopup();
  }

  updateQuantity(pid, vid, qty) {
    const item = this.items.find((i) => i.product_id == pid && i.variant_id == vid);
    if (item) {
      item.quantity = qty;
      this.save();
      this.renderPopup();
      this.updateBadge();
      this.updateHeaderBadge();
      this.updateDesktopHeaderBadge(); // Update desktop badge when quantity is changed
    }
  }

  /* -----------------------------------------------------------
     POPUP OPEN / CLOSE
  ----------------------------------------------------------- */
  openPopup() {
    if (!document.getElementById("quotePopup")) {
      this.createPopup();
    }
    this.renderPopup();
    document.getElementById("quotePopup").style.display = "flex";
    document.body.style.overflow = "hidden";
  }

  closePopup() {
    const popup = document.getElementById("quotePopup");
    if (popup) popup.style.display = "none";

    document.body.style.overflow = "auto";
  }

  /* -----------------------------------------------------------
     POPUP CREATION — FULL FORM + LAYOUT
  ----------------------------------------------------------- */
  createPopup() {
    const html = `
      <div id="quotePopup" class="quote-popup-with-form" style="display:none;">
        <div class="popup-overlay"></div>

        <div class="popup-content">
          <header class="popup-header">
            <h2 class="popup-title">Request a quote</h2>
            <button type="button" class="popup-close-btn" id="popupCloseX" aria-label="Close">×</button>
          </header>

          <form id="quoteContactForm" action="/contact" method="post" class="popup-form">
            <div class="popup-body">
              <input type="hidden" name="form_type" value="contact">
              <input type="hidden" name="utf8" value="✓">

              <!-- CONTACT INFORMATION -->
              <section class="popup-section">
                <div class="popup-eyebrow">Contact information</div>
                <div class="popup-grid">
                  <label class="popup-field popup-field--full">
                    <span class="popup-field-label">Company</span>
                    <input type="text" name="contact[company]">
                  </label>
                  <label class="popup-field">
                    <span class="popup-field-label">First name *</span>
                    <input required type="text" name="contact[first_name]">
                  </label>
                  <label class="popup-field">
                    <span class="popup-field-label">Last name *</span>
                    <input required type="text" name="contact[last_name]">
                  </label>
                  <label class="popup-field">
                    <span class="popup-field-label">Mobile *</span>
                    <input required type="tel" name="contact[phone]">
                  </label>
                  <label class="popup-field">
                    <span class="popup-field-label">Email *</span>
                    <input required type="email" name="contact[email]">
                  </label>
                </div>
              </section>

              <!-- DELIVERY INFORMATION -->
              <section class="popup-section">
                <div class="popup-eyebrow">Delivery information</div>
                <div class="popup-grid">
                  <label class="popup-field popup-field--full">
                    <span class="popup-field-label">Delivery address</span>
                    <input type="text" name="contact[address]">
                  </label>
                  <label class="popup-field">
                    <span class="popup-field-label">Equipment delivery date</span>
                    <input type="date" name="contact[delivery_date]">
                  </label>
                  <label class="popup-field">
                    <span class="popup-field-label">Delivery type</span>
                    <select name="contact[delivery_type]">
                      <option value="" disabled selected>Select…</option>
                      <option value="Kerbside">Kerbside delivery</option>
                      <option value="Premium">Premium delivery</option>
                    </select>
                  </label>
                  <label class="popup-field popup-field--full">
                    <span class="popup-field-label">Message</span>
                    <textarea name="contact[notes]" rows="3"></textarea>
                  </label>
                </div>
              </section>

              <!-- EQUIPMENT LIST -->
              <section class="popup-section">
                <div class="popup-eyebrow">Equipment list</div>
                <div class="quote-table-container">
                  <table class="quote-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>SKU</th>
                        <th>Price</th>
                        <th>Qty</th>
                        <th>Total</th>
                        <th aria-label="Remove"></th>
                      </tr>
                    </thead>
                    <tbody id="quoteTableBody"></tbody>
                  </table>

                  <div id="emptyState" class="empty-state" style="display:none;">
                    No items in quote yet.
                  </div>
                </div>
              </section>

              <!-- PREFERENCES -->
              <section class="popup-section">
                <div class="popup-eyebrow">Preferences</div>

                <div class="popup-prefgroup">
                  <div class="popup-prefgroup-label">Finance</div>
                  <label class="popup-check">
                    <input type="checkbox" name="contact[finance]" value="yes">
                    <span>I'm interested in finance</span>
                  </label>
                </div>

                <hr class="popup-divider">

                <div class="popup-prefgroup">
                  <div class="popup-prefgroup-label">Preferred contact method *</div>
                  <div class="popup-prefgroup-options">
                    <label class="popup-check">
                      <input type="radio" required name="contact[preference]" value="callback">
                      <span>I would like a callback</span>
                    </label>
                    <label class="popup-check">
                      <input type="radio" required name="contact[preference]" value="email">
                      <span>Please email my quote (no callback)</span>
                    </label>
                  </div>
                </div>
              </section>

              <!-- Email Body (hidden full summary) -->
              <textarea name="contact[body]" id="quoteBody" style="display:none;"></textarea>
            </div>

            <footer class="popup-footer">
              <button type="submit" id="submitQuoteBtn" class="popup-btn popup-btn--primary">
                Submit quote request
              </button>
              <button type="button" id="keepShoppingBtn" class="popup-btn popup-btn--ghost">
                Keep shopping
              </button>
              <button id="goToQuotePageBtn" type="button" class="popup-btn popup-btn--ghost">
                Add my cart to this quote
              </button>
            </footer>
          </form>
        </div>
      </div>
    `;

    document.body.insertAdjacentHTML("beforeend", html);

    document.querySelector(".popup-overlay").addEventListener("click", () => this.closePopup());
    document.getElementById("popupCloseX").addEventListener("click", () => this.closePopup());
    document.getElementById("keepShoppingBtn").addEventListener("click", () => this.closePopup());

    // Persist form values across page reloads / product navigation.
    const form = document.getElementById("quoteContactForm");
    if (form) {
      this.restoreForm(form);
      form.addEventListener("input", () => this.saveForm(form));
      form.addEventListener("change", () => this.saveForm(form));
    }
  }

  /* -----------------------------------------------------------
     FORM PERSISTENCE
  ----------------------------------------------------------- */
  saveForm(form) {
    const data = {};
    new FormData(form).forEach((value, key) => {
      // Skip the hidden email-body summary (rebuilt on submit) and the
      // form_type/utf8 housekeeping fields.
      if (key === "contact[body]" || key === "form_type" || key === "utf8") return;
      data[key] = value;
    });
    // Checkboxes don't appear in FormData when unchecked — capture state explicitly.
    form.querySelectorAll('input[type="checkbox"]').forEach((cb) => {
      data["__checked__" + cb.name] = cb.checked ? "1" : "0";
    });
    try { localStorage.setItem(this.formKey, JSON.stringify(data)); } catch (e) {}
  }

  restoreForm(form) {
    let data;
    try { data = JSON.parse(localStorage.getItem(this.formKey) || "{}"); }
    catch (e) { data = {}; }

    Object.keys(data).forEach((key) => {
      if (key.indexOf("__checked__") === 0) {
        const name = key.slice("__checked__".length);
        const cb = form.querySelector('input[type="checkbox"][name="' + CSS.escape(name) + '"]');
        if (cb) cb.checked = data[key] === "1";
        return;
      }
      const el = form.querySelector('[name="' + CSS.escape(key) + '"]');
      if (!el) return;
      if (el.type === "radio") {
        const radio = form.querySelector('[name="' + CSS.escape(key) + '"][value="' + CSS.escape(data[key]) + '"]');
        if (radio) radio.checked = true;
      } else if (el.tagName === "SELECT") {
        el.value = data[key];
      } else {
        el.value = data[key];
      }
    });
  }

  clearForm() {
    try { localStorage.removeItem(this.formKey); } catch (e) {}
  }

  /* -----------------------------------------------------------
     POPUP TABLE RENDERING
  ----------------------------------------------------------- */
  renderPopup() {
    const tbody = document.getElementById("quoteTableBody");
    const emptyState = document.getElementById("emptyState");
    if (!tbody) return;

    const submitBtn = document.getElementById("submitQuoteBtn");
    if (submitBtn) {
      submitBtn.disabled = this.items.length === 0;
      submitBtn.setAttribute("aria-disabled", this.items.length === 0);
      submitBtn.title = this.items.length === 0 ? "Add at least one product to request a quote" : "";
    }

    if (this.items.length === 0) {
      tbody.innerHTML = "";
      emptyState.style.display = "block";
      return;
    }

    emptyState.style.display = "none";

    let html = "";
    this.items.forEach((item) => {
      const total = item.price * item.quantity;

      html += `
        <tr class="quote-item-row" data-product="${item.product_id}" data-variant="${item.variant_id}">
          <td class="col-product">
            <div class="product-cell">
              <div class="product-image">
                <img src="${item.image}" alt="" width="56" height="56">
              </div>
              <div class="product-info">
                <div class="product-title">${item.title}</div>
                ${item.dimensions ? `<div class="product-dimensions">${item.dimensions}</div>` : ""}
              </div>
            </div>
          </td>
          <td class="col-sku">${item.sku || ""}</td>
          <td class="col-price">${item.formatted_price}</td>
          <td class="col-qty">
            <div class="quantity-control">
              <button type="button" class="qty-btn minus" aria-label="Decrease">−</button>
              <input type="number" class="qty-input" value="${item.quantity}" min="1">
              <button type="button" class="qty-btn plus" aria-label="Increase">+</button>
            </div>
          </td>
          <td class="col-total">$${(total / 100).toFixed(2)}</td>
          <td class="col-remove">
            <button type="button" class="remove-btn" aria-label="Remove">×</button>
          </td>
        </tr>
      `;
    });

    tbody.innerHTML = html;
  }

  /* -----------------------------------------------------------
     FORM SUBMISSION HANDLER - NEW: This is the fix!
  ----------------------------------------------------------- */
  handleFormSubmit(form) {
    // Build the form data
    const formData = new FormData(form);
    
    // Build the email body with product data
    const emailBody = this.buildEmailBody(formData);
    
    // Set the email body to the hidden textarea
    const quoteBodyTextarea = document.getElementById('quoteBody');
    if (quoteBodyTextarea) {
      quoteBodyTextarea.value = emailBody;
    }
    
    // Persisted form is for in-progress sessions only — wipe it now that the
    // user has actually submitted. The /contact POST redirects, so this runs
    // just before navigation.
    this.clearForm();

    if (window.WhiskLeads) window.WhiskLeads.mark('quote');

    // Now submit the form with the populated product data
    form.submit();
    
    // Optional: Clear the cart after successful submission
    // this.items = [];
    // this.save();
    // this.updateBadge();
    // this.updateHeaderBadge();
    // this.updateDesktopHeaderBadge();
    // this.closePopup();
  }

  /* -----------------------------------------------------------
     EMAIL BODY BUILDER
  ----------------------------------------------------------- */
  buildEmailBody(formData) {
    let body = `NEW QUOTE REQUEST\n`;
    body += `========================================\n\n`;

    /* CONTACT INFORMATION */
    body += `CONTACT INFORMATION:\n`;
    body += `----------------------------------------\n`;
    body += `First Name: ${formData.get("contact[first_name]")}\n`;
    body += `Last Name: ${formData.get("contact[last_name]")}\n`;
    body += `Company: ${formData.get("contact[company]")}\n`;
    body += `Phone: ${formData.get("contact[phone]")}\n`;
    body += `Email: ${formData.get("contact[email]")}\n\n`;

    /* DELIVERY INFORMATION */
    body += `DELIVERY INFORMATION:\n`;
    body += `----------------------------------------\n`;
    body += `Address: ${formData.get("contact[address]")}\n`;
    body += `Delivery Date: ${formData.get("contact[delivery_date]")}\n`;
    body += `Delivery Type: ${formData.get("contact[delivery_type]")}\n`;
    body += `Message: ${formData.get("contact[notes]")}\n\n`;

    /* FINANCE + PREF */
    body += `PREFERENCES:\n`;
    body += `----------------------------------------\n`;
    body += `Interested in Finance: ${formData.get("contact[finance]") ? "Yes" : "No"}\n`;
    body += `Preferred Contact: ${formData.get("contact[preference]")}\n\n`;

    /* PRODUCT LIST */
    body += `EQUIPMENT LIST:\n`;
    body += `========================================\n\n`;

    this.items.forEach((item, i) => {
      const total = item.price * item.quantity;

      body += `ITEM ${i + 1}:\n`;
      body += `Product ID: ${item.product_id}\n`;
      body += `Variant ID: ${item.variant_id}\n`;
      body += `Image URL: ${item.image}\n`;
      body += `Title: ${item.title}\n`;
      body += `SKU: ${item.sku}\n`;
      body += `Unit Price: ${item.formatted_price}\n`;
      body += `Quantity: ${item.quantity}\n`;
      body += `Total: $${(total / 100).toFixed(2)}\n`;
      if (item.dimensions) {
        body += `Dimensions: ${item.dimensions}\n`;
      }
      body += `----------------------------------------\n\n`;
    });

    /* TOTAL SUMMARY */
    const grandTotal = this.items.reduce((sum, item) => sum + (item.price * item.quantity), 0);

    body += `TOTAL ITEMS: ${this.items.length}\n`;
    body += `GRAND TOTAL VALUE: $${(grandTotal / 100).toFixed(2)}\n\n`;

    return body;
  }
}

/* ============================================================
   Initialise, then announce readiness.

   `window.completeQuoteCart` is kept exactly as it was, so every existing consumer
   keeps working unchanged. What is new is a way to *wait* for it.

   Before this extraction, sections rendered inside content_for_layout ran before this
   code and had to poll for the global — main-product-v2.liquid retried 40 times at 50ms
   and then gave up and rendered an empty state, which is the documented load-order race.

   layout/theme.liquid creates window.whiskQuote.ready in <head> before anything can ask
   for it; this resolves it. Consumers do:

       window.whiskQuote.ready.then(function (cart) { ... });

   which cannot race regardless of script order.
   ============================================================ */
window.completeQuoteCart = new CompleteQuoteCart();

window.whiskQuote = window.whiskQuote || {};
window.whiskQuote.cart = window.completeQuoteCart;
if (typeof window.whiskQuote._resolve === 'function') {
  window.whiskQuote._resolve(window.completeQuoteCart);
} else {
  // Bootstrap missing (e.g. this file loaded standalone). Provide a resolved promise so
  // consumers still work rather than hanging forever.
  window.whiskQuote.ready = Promise.resolve(window.completeQuoteCart);
}
document.dispatchEvent(
  new CustomEvent('whisk:quote:ready', { detail: { cart: window.completeQuoteCart } })
);
